import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {REMIT_BASE_URL} from './config';
import {PartnerRole} from './remit-agency-partner.service';
import {PaginationParams} from './remit-invoice.service';

export type CommissionEvaluationDecision = 'created' | 'skipped';

export type CommissionEvaluationReasonCode =
  | 'ok'
  | 'invalid_payload'
  | 'storage_misconfigured'
  | 'not_paid_transition'
  | 'already_commissioned'
  | 'member_query_failed'
  | 'member_not_found'
  | 'no_agency_referral'
  | 'partner_lookup_failed'
  | 'partner_inactive'
  | 'invalid_partner'
  | 'agent_missing_parent'
  | 'commission_calculation_failed'
  | 'commission_amount_zero'
  | 'commission_write_failed'
  | 'duplicate_event'
  | 'unknown_error';

export interface CommissionEvaluationEntry {
  partnerId: string;
  partnerRole: PartnerRole;
  parentPartnerId?: string;
  agencyCode: string;
  amountTWD: string;
  currency: string;
  decision: CommissionEvaluationDecision;
  reasonCode: CommissionEvaluationReasonCode;
  reasonDetail?: string;
  commissionId?: string;
  superCommissionPerTxnTWD?: string;
  agentCommissionPerTxnTWD?: string;
}

export interface CommissionEvaluation {
  id?: string;
  invoiceId: string;
  userId: string;
  messageId: string;
  program: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  decision: CommissionEvaluationDecision;
  reasonCode: CommissionEvaluationReasonCode;
  reasonDetail?: string;
  entries: CommissionEvaluationEntry[];
  version?: string;
}

export interface CommissionEvaluationQueryParams extends PaginationParams {
  invoiceId?: string;
  userId?: string;
  partnerId?: string;
  program?: string;
  decision?: CommissionEvaluationDecision;
  reasonCode?: CommissionEvaluationReasonCode;
  createdAtStart?: string;
  createdAtEnd?: string;
  sortBy?: string;
  sortDirection?: number;
}

export interface CommissionEvaluationQueryResponse {
  message: string;
  body: CommissionEvaluation[];
  totalData: number;
}

@Injectable({
  providedIn: 'root',
})
export class RemitCommissionEvaluationService {
  constructor(
    private http: HttpClient,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) {}

  /**
   * Query commission evaluation logs with filters and pagination.
   * @param {CommissionEvaluationQueryParams} params - Query parameters
   * @return {Observable<CommissionEvaluationQueryResponse>} Query response
   */
  queryEvaluations(
      params: CommissionEvaluationQueryParams,
  ): Observable<CommissionEvaluationQueryResponse> {
    return this.http
        .post<CommissionEvaluationQueryResponse>(
            `${this.remitApiUrl}/staff/commission-evaluations`,
            params,
        )
        .pipe(
            map((response) => {
              if (!response.body || response.body.length === 0) {
                return {
                  message: response.message,
                  body: [],
                  totalData: 0,
                };
              }
              return response;
            }),
        );
  }
}
